import { Disclosure } from '@headlessui/react';
import { Fragment, useCallback, useMemo } from 'react';
import { Button } from '../../../../../core/components/atoms/Button';
import { Icon } from '../../../../../core/components/atoms/Icon';
import { Typography } from '../../../../../core/components/molecules/Typography';
import { Modal } from '../../../../../core/components/templates/Modal';
import { useExerciseStore } from '../../../stores/exercisesStore';
import InnerCheckbox from './innerCheckbox';
import MultipleSelect, { IMultipleOptions } from '../../../../../core/components/atoms/MultipleSelect';
import { listExercisesYear } from '../../../utils/exerciseHelper';
import { useFetchSubareaInstitutions } from '../../../../../core/hooks/useFetchSubareaInstitutions';
import { useNewSubjectStore } from '../../../stores/newSubjectStore';

interface IFilterModal {
  isOpen: boolean;
  onClose: () => void;
  subtitle: string;
  onSubmit: () => void;
  slug?: string;
}

export default function FilterModal({
  isOpen,
  onClose,
  subtitle,
  onSubmit,
  slug,
}: IFilterModal) {
  const {
    favorite,
    difficulty,
    unanswered,
    rightAnswer,
    wrongAnswer,
    questionOptions,
    isEnem,
    selectedYear,
    institutionType,
    setFavorite,
    setDifficulty,
    setUnanswered,
    setRightAnswer,
    setWrongAnswer,
    setQuestionOptions,
    setIsEnem,
    setSelectedYear,
    setInstitutionType
  } = useExerciseStore();

  const { selectedSubjects } = useNewSubjectStore();

  const yearOptionsRaw = listExercisesYear();
  const yearOptions: IMultipleOptions[] = useMemo(() =>
    yearOptionsRaw
      .filter((opt) => opt.value !== 'all')
      .map((opt) => ({ value: String(opt.value), label: opt.label })),
    [yearOptionsRaw]
  );

  // Converte isEnem para exerciseType compatível com o endpoint
  const exerciseType = useMemo(() => {
    const hasEnem = isEnem.includes('enem');
    const hasVestibulares = isEnem.includes('vestibulares');

    if (hasEnem && !hasVestibulares) return 'enem';
    if (hasVestibulares && !hasEnem) return 'vest';
    return null;
  }, [isEnem]);

  const fetchInstitutions = useFetchSubareaInstitutions({
    slug,
    selectedYear,
    exerciseType,
    difficulty,
    questionOptions,
    favorite,
    unanswered,
    rightAnswer,
    wrongAnswer,
    selectedSubjects,
    enabled: isEnem.includes('vestibulares'),
  });

  const institutionsOptions = useMemo<IMultipleOptions[]>(() => {
    const institutions = fetchInstitutions.data ?? [];
    return institutions.map((inst: { value: string | number; label: string }) => ({
      value: String(inst.value),
      label: inst.label,
    }));
  }, [fetchInstitutions.data]);

  const onChangeYear = useCallback(
    (values: string[]) => {
      setSelectedYear(values);
      setInstitutionType([]);
    },
    [setSelectedYear, setInstitutionType]
  );

  const difficultyHandler = (level: string) => {
    if (difficulty.indexOf(level) >= 0) {
      const array = [...difficulty];

      array.splice(difficulty.indexOf(level), 1);
      setDifficulty(array);
    } else {
      const array = [...difficulty];
      array.push(level);

      setDifficulty(array);
    }
  };

  const isEnemHandler = (option: string) => {
    if (isEnem.indexOf(option) >= 0) {
      const array = [...isEnem];

      array.splice(isEnem.indexOf(option), 1);
      if(!array.includes('vestibular')){
        setInstitutionType([]);
      }
      setIsEnem(array);
    } else {
      const array = [...isEnem];
      array.push(option);

      setIsEnem(array);
    }
  };

  const questionHandler = (type: string) => {
    const aux = [...questionOptions];
    const idx = aux.find((it) => it === type);

    if(idx) {
      const newArray = aux.filter((it) => it !== type)
      setQuestionOptions(newArray);
    } else {
      aux.push(type);
      setQuestionOptions(aux);
    }
  };

  const reset = () => {
    setFavorite(false);
    setUnanswered(false);
    setRightAnswer(false);
    setWrongAnswer(false);
    setDifficulty([]);
    setQuestionOptions([]);
    setIsEnem([]);
    setSelectedYear([]);
    setInstitutionType([]);
  };

  const submit = () => {
    onSubmit();
    onClose();
  };

  return (
    <Modal
      position='top-right'
      size='md'
      isOpen={isOpen}
      onClose={() => onClose()}
      className='   
        m-0 
        mt-[10vh] 
        flex 
        min-h-[90vh]
        
        max-w-full 
        flex-col 
        rounded-b-none 
        md:m-4
        md:min-h-[calc(100%-2rem)]
    
        md:max-w-lg 
        md:rounded-b-lg'
      title='Filtrar exercícios'
      subtitle={subtitle}
    >
      <div className='flex w-full flex-col gap-md overflow-x-auto px-4 py-5 xs:h-[45vh] lg:h-[75vh]'>
        <Disclosure defaultOpen>
          <Disclosure.Panel className='flex w-full flex-col gap-xl pb-3'>
            <InnerCheckbox
              onChange={() => setFavorite(!favorite)}
              label='Favoritados'
              checked={favorite}
              id='favorite'
              value={'favorite'}
            />
          </Disclosure.Panel>
        </Disclosure>
        <Disclosure defaultOpen>
          {({ open }) => (
            <Fragment>
              <Disclosure.Button className='flex w-full justify-between text-left'>
                <Typography size='h4' type='title' className='text-left'>
                  Tipo de questão
                </Typography>
                <Icon
                  Icon='expand_more'
                  type='material'
                  className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
                />
              </Disclosure.Button>
              <Disclosure.Panel className='mt-5 flex w-full flex-col gap-xl pb-3 pl-4'>
                <InnerCheckbox
                  onChange={() => isEnemHandler('enem')}
                  label='Enem'
                  checked={isEnem.includes('enem')}
                  id='type-enem'
                  value={'enem'}
                />
                <InnerCheckbox
                  onChange={() => isEnemHandler('vestibulares')}
                  label='Vestibulares'
                  checked={isEnem.includes('vestibulares')}
                  id='type-vestibulares'
                  value={'vestibulares'}
                />
              </Disclosure.Panel>
            </Fragment>
          )}
        </Disclosure>
        <Disclosure defaultOpen>
          {({ open }) => (
            <Fragment>
              <Disclosure.Button className='flex w-full justify-between text-left'>
                <Typography size='h4' type='title' className='text-left'>
                  Ano
                </Typography>
                <Icon
                  Icon='expand_more'
                  type='material'
                  className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
                />
              </Disclosure.Button>
              <Disclosure.Panel className='mt-5 flex w-full flex-col gap-xl pb-3 pl-4'>
                <MultipleSelect
                  options={yearOptions}
                  label='Selecione o ano'
                  placeholder='Todos os anos'
                  onChange={onChangeYear}
                  selected={selectedYear}
                  className="xs:w-full lg:w-full"
                />
              </Disclosure.Panel>
            </Fragment>
          )}
        </Disclosure>
        {isEnem.includes('vestibulares') && (
          <Disclosure defaultOpen>
            {({ open }) => (
              <Fragment>
                <Disclosure.Button className='flex w-full justify-between text-left'>
                  <Typography size='h4' type='title' className='text-left'>
                    Instituição
                  </Typography>
                  <Icon
                    Icon='expand_more'
                    type='material'
                    className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
                  />
                </Disclosure.Button>
                <Disclosure.Panel className='mt-5 flex w-full flex-col gap-xl pb-3 pl-4'>
                  <MultipleSelect
                    options={institutionsOptions}
                    label='Selecione a instituição'
                    placeholder='Todas as instituições'
                    onChange={(values) => setInstitutionType(values)}
                    selected={institutionType}
                    className="xs:w-full lg:w-full"
                    disabled={fetchInstitutions.isLoading}
                  />
                </Disclosure.Panel>
              </Fragment>
            )}
          </Disclosure>
        )}
        <Disclosure defaultOpen>
          {({ open }) => (
            <Fragment>
              <Disclosure.Button className='flex w-full justify-between text-left'>
                <Typography size='h4' type='title' className='text-left'>
                  Resolução
                </Typography>
                <Icon
                  Icon='expand_more'
                  type='material'
                  className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
                />
              </Disclosure.Button>
              <Disclosure.Panel className='mt-5 flex w-full flex-col gap-xl pb-3 pl-4'>
                <InnerCheckbox
                  onChange={() => setUnanswered(!unanswered)}
                  label='Não respondidos'
                  checked={unanswered}
                  id='resolution-notanswered'
                  value={'unanswered'}
                />
                <InnerCheckbox
                  onChange={() => setWrongAnswer(!wrongAnswer)}
                  label='Respostas erradas'
                  checked={wrongAnswer}
                  id='resolution-wrong'
                  value='wrongAnswer'
                />
                <InnerCheckbox
                  onChange={() => setRightAnswer(!rightAnswer)}
                  label='Respostas certas'
                  checked={rightAnswer}
                  id='resolution-right'
                  value='rightAnswer'
                />
              </Disclosure.Panel>
            </Fragment>
          )}
        </Disclosure>
        <Disclosure defaultOpen>
          {({ open }) => (
            <Fragment>
              <Disclosure.Button className='flex w-full justify-between text-left'>
                <Typography size='h4' type='title' className='text-left'>
                  Nível de dificuldade
                </Typography>
                <Icon
                  Icon='expand_more'
                  type='material'
                  className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
                />
              </Disclosure.Button>
              <Disclosure.Panel className='mt-5 flex w-full flex-col gap-xl pb-3 pl-4'>
                <InnerCheckbox
                  onChange={() => difficultyHandler('easy')}
                  label='Fácil'
                  checked={difficulty.indexOf('easy') >= 0}
                  id='difficulty-easy'
                  value='easy'
                />
                <InnerCheckbox
                  onChange={() => difficultyHandler('medium')}
                  label='Médio'
                  checked={difficulty.indexOf('medium') >= 0}
                  id='difficulty-medium'
                  value='medium'
                />
                <InnerCheckbox
                  onChange={() => difficultyHandler('hard')}
                  label='Difícil'
                  checked={difficulty.indexOf('hard') >= 0}
                  id='difficulty-hard'
                  value='hard'
                />
                <InnerCheckbox
                  onChange={() => difficultyHandler('random')}
                  label='Aleatório'
                  checked={difficulty.indexOf('random') >= 0}
                  id='difficulty-random'
                  value='random'
                />
              </Disclosure.Panel>
            </Fragment>
          )}
        </Disclosure>
        <Disclosure defaultOpen>
          {({ open }) => (
            <Fragment>
              <Disclosure.Button className='flex w-full justify-between text-left'>
                <Typography size='h4' type='title' className='text-left'>
                  Tipo de exercício
                </Typography>
                <Icon
                  Icon='expand_more'
                  type='material'
                  className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
                />
              </Disclosure.Button>
              <Disclosure.Panel className='mt-5 flex w-full flex-col gap-xl pb-3 pl-4'>
                <InnerCheckbox
                  onChange={() => questionHandler('single_answer')}
                  label='Resposta única'
                  checked={questionOptions.indexOf('single_answer') >= 0}
                  id='type-single-answer'
                  value='single_answer'
                />
                <InnerCheckbox
                  onChange={() => questionHandler('multiple_answer')}
                  label='Resposta somatória'
                  checked={questionOptions.indexOf('multiple_answer') >= 0}
                  id='type-multiple-answer'
                  value='multiple_answer'
                />
                <InnerCheckbox
                  onChange={() => questionHandler('discursive')}
                  label='Resposta discursiva'
                  checked={questionOptions.indexOf('discursive') >= 0}
                  id='type-discursive'
                  value='discursive'
                />
              </Disclosure.Panel>
            </Fragment>
          )}
        </Disclosure>
      </div>
      <div className='fixed bottom-0 left-0 flex w-full flex-row items-center justify-center gap-lg px-4 py-5'>
        <Button
          variant='text'
          LeftIcon='delete'
          iconType='material'
          className='w-1/2'
          full
          onClick={() => reset()}
        >
          Limpar seleção
        </Button>
        <Button className='w-1/2' full onClick={() => submit()}>
          Aplicar filtros
        </Button>
      </div>
    </Modal>
  );
}
